// FilterChip — 22px monospace pill that summarises a single active facet.
//
// Used by FilterBar (inline strip mode) and IntentBar (sentence mode).
// Click → opens an edit popover (host wires the popover; chip just emits).

import type { CSSProperties, ReactNode } from 'react';

export interface FilterChipProps {
  label: string; // "AGY"
  value: ReactNode; // "DoD, DOE" or a node with truncation
  tone?: 'primary' | 'amber' | 'red' | 'neutral';
  onEdit?: () => void;
  onRemove?: () => void;
  ariaLabel?: string;
}

export function FilterChip({
  label,
  value,
  tone = 'neutral',
  onEdit,
  onRemove,
  ariaLabel,
}: FilterChipProps) {
  const tint = TINT[tone];
  return (
    <span
      role="group"
      aria-label={ariaLabel ?? `filter ${label} ${typeof value === 'string' ? value : ''}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 22,
        gap: 6,
        padding: '0 4px 0 6px',
        background: tint.bg,
        border: `1px solid ${tint.bd}`,
        color: 'var(--fg-1)',
        fontFamily: 'var(--mono)',
        fontSize: 11,
        whiteSpace: 'nowrap',
        maxWidth: 240,
      }}
    >
      <button
        type="button"
        onClick={onEdit}
        title={`edit ${label}`}
        style={{
          ...buttonReset,
          color: 'var(--fg-3)',
          fontSize: 10,
          letterSpacing: 0.4,
        }}
      >
        {label.toUpperCase()}
      </button>
      <span
        style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
        onClick={onEdit}
        title={typeof value === 'string' ? value : undefined}
      >
        {value}
      </span>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`remove ${label} filter`}
          title="remove"
          style={{ ...buttonReset, color: 'var(--fg-3)', fontSize: 12, padding: '0 2px' }}
        >
          ×
        </button>
      )}
    </span>
  );
}

const buttonReset: CSSProperties = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  fontFamily: 'var(--mono)',
};

const TINT: Record<NonNullable<FilterChipProps['tone']>, { bg: string; bd: string }> = {
  primary: {
    bg: 'var(--primary-bg-faint, rgba(52,120,246,0.08))',
    bd: 'var(--primary-line, rgba(52,120,246,0.45))',
  },
  amber: { bg: 'rgba(255,176,0,0.10)', bd: 'rgba(255,176,0,0.45)' },
  red: { bg: 'rgba(245,86,86,0.10)', bd: 'rgba(245,86,86,0.45)' },
  neutral: { bg: 'var(--bg-pane-2)', bd: 'var(--bd-strong)' },
};
